import { useState } from "react";
import { api } from "@/lib/api";
import { Mail, CheckCircle2 } from "lucide-react";
import { toast } from "sonner";

/**
 * NewsletterSignup — inline double-opt-in signup form for the Blog.
 *
 * Props:
 *   variant: "wide" | "compact"  — layout, defaults to "wide"
 *   surface: "paper" | "alt"     — background container, defaults to "alt"
 */
export default function NewsletterSignup({ variant = "wide", surface = "alt" }) {
  const [email, setEmail] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [done, setDone] = useState(false);
  const [message, setMessage] = useState("");

  const onSubmit = async (e) => {
    e.preventDefault();
    if (!email.trim()) return;
    setSubmitting(true);
    try {
      const { data } = await api.post("/newsletter/subscribe", {
        email: email.trim().toLowerCase(),
      });
      setDone(true);
      setMessage(
        data?.message || "Check your inbox — confirm to finish subscribing."
      );
      setEmail("");
    } catch (err) {
      const detail = err?.response?.data?.detail;
      const msg =
        typeof detail === "string"
          ? detail
          : "Something went wrong. Please try again.";
      toast.error(msg);
    } finally {
      setSubmitting(false);
    }
  };

  const bg = surface === "alt" ? "bg-paper-alt" : "bg-paper";

  if (done) {
    return (
      <div
        className={`${bg} border hairline-strong p-6 md:p-8`}
        data-testid="newsletter-success"
      >
        <div className="flex items-start gap-4">
          <div className="mt-1 shrink-0 h-9 w-9 rounded-full bg-brand-red text-paper flex items-center justify-center">
            <CheckCircle2 size={18} aria-hidden />
          </div>
          <div>
            <p className="eyebrow">§ Almost there</p>
            <h3 className="font-display text-2xl md:text-3xl mt-2">
              Check your inbox.
            </h3>
            <p className="mt-2 font-body text-ink-soft leading-relaxed max-w-prose">
              {message}
            </p>
          </div>
        </div>
      </div>
    );
  }

  return (
    <form
      onSubmit={onSubmit}
      data-testid="newsletter-form"
      className={`${bg} border hairline-strong p-6 md:p-8`}
    >
      <div className={variant === "compact" ? "" : "md:flex md:items-end md:gap-8"}>
        <div className={variant === "compact" ? "" : "md:flex-1"}>
          <p className="eyebrow">§ The Redkey list</p>
          <h3 className="font-display text-2xl md:text-3xl mt-2">
            New entries, sent when they&rsquo;re{" "}
            <em className="not-italic text-brand-red">worth reading.</em>
          </h3>
          <p className="mt-3 font-body text-ink-soft max-w-prose">
            One email per new post. No pitches, no drip, no unsubscribe games.
          </p>
        </div>
        <div
          className={
            variant === "compact"
              ? "mt-5 flex flex-col sm:flex-row gap-3"
              : "mt-6 md:mt-0 md:w-[420px] flex flex-col sm:flex-row gap-3"
          }
        >
          <div className="relative flex-1">
            <Mail
              size={16}
              aria-hidden
              className="absolute left-3 top-1/2 -translate-y-1/2 text-label"
            />
            <input
              type="email"
              required
              maxLength={200}
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              placeholder="your@email.com"
              aria-label="Email address"
              data-testid="newsletter-email"
              className="w-full bg-paper border hairline-strong focus:border-electric outline-none pl-10 pr-3 py-3 font-body"
            />
          </div>
          <button
            type="submit"
            disabled={submitting || !email.trim()}
            data-testid="newsletter-submit"
            className="inline-flex items-center justify-center gap-2 bg-ink text-paper px-6 py-3 hover:bg-electric transition-colors disabled:opacity-60 font-ui shrink-0"
          >
            {submitting ? "Sending…" : "Subscribe"}
          </button>
        </div>
      </div>
      <p className="mt-4 font-ui text-[11px] text-label tracking-widest uppercase">
        Double opt-in · Unsubscribe in one click · No sharing, ever
      </p>
    </form>
  );
}
